import { useRef, useState } from 'react';
import { CAPABILITIES } from '../content';
import { prefersReducedMotion, useAutoCycle, useInView } from '../hooks';
import Fit from './Fit';
import Reveal from './Reveal';
import { ArrowRight } from './Icons';
import { ApiScreen, OpsScreen, SCREEN_SIZE, WatchScreen, WeightBalanceScreen } from './Screens';
import './Capabilities.css';

// The hero already shows the briefing overview, so the EFB slot shows another view of the same app.
const SCREENS = { efb: WeightBalanceScreen, wearables: WatchScreen, ops: OpsScreen, data: ApiScreen };
const CYCLE_MS = 7000;

export function Accordion({ items, active, onSelect, running, cycle, renderLead, idPrefix }) {
  return (
    <ul className="acc">
      {items.map((item, i) => {
        const open = i === active;
        return (
          <li key={item.title} className={`acc__item${open ? ' is-active' : ''}`}>
            <button
              type="button"
              className="acc__btn"
              aria-expanded={open}
              aria-controls={`${idPrefix}-${i}`}
              onClick={() => onSelect(i)}
            >
              {renderLead ? renderLead(item, i) : <span className="acc__dot" aria-hidden="true" />}
              <span>{item.title}</span>
            </button>
            <div id={`${idPrefix}-${i}`} className="acc__panel" role="region" aria-hidden={!open}>
              <div>
                <p>{item.body}</p>
              </div>
            </div>
            {open && (
              <span className="acc__progress" aria-hidden="true">
                <i
                  key={`${cycle}-${running}`}
                  className={running ? 'is-running' : ''}
                  style={{ animationDuration: `${CYCLE_MS}ms` }}
                />
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function useAccordionCycle(ref, count) {
  const visible = useInView(ref, { once: false, threshold: 0.35 });
  const [paused, setPaused] = useState(false);
  const running = visible && !paused && !prefersReducedMotion();
  const [active, select, cycle] = useAutoCycle(count, CYCLE_MS, running);
  const hoverProps = {
    onMouseEnter: () => setPaused(true),
    onMouseLeave: () => setPaused(false),
    onFocus: () => setPaused(true),
    onBlur: () => setPaused(false),
  };
  return { active, select, cycle, running, hoverProps };
}

export default function Capabilities() {
  const ref = useRef(null);
  const { active, select, cycle, running, hoverProps } = useAccordionCycle(ref, CAPABILITIES.length);

  return (
    <section id="capabilities" ref={ref} className="section tone-dark caps" data-nav="dark">
      <div className="container caps__grid">
        <div className="caps__copy">
          <Reveal className="section-head">
            <span className="eyebrow">What we build</span>
            <h2 className="h2">
              Software for every <em>phase of flight</em>.
            </h2>
          </Reveal>
          <Reveal delay={120} {...hoverProps}>
            <Accordion
              items={CAPABILITIES}
              active={active}
              onSelect={select}
              running={running}
              cycle={cycle}
              idPrefix="cap"
            />
          </Reveal>
          <Reveal delay={200}>
            <a href="#contact" className="link-arrow">
              Talk to an engineer <ArrowRight />
            </a>
          </Reveal>
        </div>

        <Reveal className="caps__visual" delay={160} aria-hidden="true">
          {CAPABILITIES.map((c, i) => {
            const Screen = SCREENS[c.id];
            return (
              <div key={c.id} className={`caps__screen${i === active ? ' is-active' : ''}`}>
                <Fit w={SCREEN_SIZE.w} h={SCREEN_SIZE.h}>
                  <Screen />
                </Fit>
              </div>
            );
          })}
        </Reveal>
      </div>
    </section>
  );
}
